import { routes } from "@/router";
import { Trans, useTranslation } from "react-i18next";
import { HiOutlineArrowRight, HiOutlineCube } from "react-icons/hi2";
import { generatePath, useNavigate } from "react-router-dom";

import { Campaign } from "@/hooks/api/predictive/product/useGetAllProducts";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import CampaignCardSettings from "@/components/molecules/predictive/CampaignCardSettings";
import CampaignStatusBadge from "@/components/molecules/predictive/CampaignStatusBadge";

type CampaignCardProps = {
  productId: string;
  campaign: Campaign;
};

const CampaignCard = ({ productId, campaign }: CampaignCardProps) => {
  const { description, status, next_list_release, name } = campaign;
  const listAmount = 0;
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={"relative space-y-2 rounded-lg border p-7 shadow-sm"}>
      <div className={"flex justify-between gap-6"}>
        <FeaturedIcon icon={HiOutlineCube} size={"xl"} color={status !== "ACTIVE" ? "natural" : "primary"} />

        {status !== "DRAFT" && <CampaignCardSettings campaign={campaign} productId={productId} />}
      </div>

      <div className={"flex items-center gap-2"}>
        <h3 className={"text-xl font-semibold text-primary"}>{name}</h3>
        <div>
          <CampaignStatusBadge campaign={campaign} />
        </div>
      </div>
      <p className={"line-clamp-3 text-sm text-quaternary"}>{description}</p>
      {status === "ACTIVE" ? (
        listAmount > 0 ? (
          <p className={"text-xs text-quaternary"}>
            <Trans
              i18nKey={"predictive.campaign.firstList"}
              values={{ date: new Date(next_list_release).toLocaleDateString() }}
              components={{ strong: <strong /> }}
            />
          </p>
        ) : (
          <p className={"text-xs text-quaternary"}>
            <Trans
              i18nKey={"predictive.campaign.nextList"}
              values={{ date: new Date(next_list_release).toLocaleDateString() }}
              components={{ strong: <strong /> }}
            />
          </p>
        )
      ) : null}

      {status === "INACTIVE" && (
        <p className={"text-xs text-quaternary"}>
          <Trans
            i18nKey={"predictive.campaign.lastList"}
            values={{ date: new Date(next_list_release).toLocaleDateString() }}
            components={{ strong: <strong /> }}
          />
        </p>
      )}

      {status === "DRAFT" ? (
        <Button
          label={t("predictive.campaign.finishSetup")}
          variants={"text"}
          className={"!px-0 !py-1"}
          trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
          onClick={() => navigate(generatePath(routes.campaign.update, { productId, campaignId: campaign.id }))}
        />
      ) : (
        <Button
          label={t("predictive.campaign.viewLeads")}
          variants={"text"}
          className={"!px-0 !py-1"}
          trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
          onClick={() => navigate(generatePath(routes.campaign.listOverview, { productId, campaignId: campaign.id }))}
        />
      )}
    </div>
  );
};

export default CampaignCard;
